import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Clock, TrendingUp, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, packagesRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/packages`),
        ]);
        setTestimonials(testimonialsRes.data);
        setPackages(packagesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  return (
    <div className="bg-[#f5f3ee]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/bgsio1k1_shutterstock_2616918681-2048x1364.jpg.jpeg"
            alt="Algarve coastline aerial view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span className="inline-block px-5 py-2 border border-[#f5f3ee]/40 text-[#f5f3ee] text-xs tracking-[0.3em] uppercase mb-8">
              Algarve, Portugal
            </span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#f5f3ee] mb-8 tracking-wide leading-[1.1]">
              Play Padel.<br />
              <span className="italic">Travel Better.</span>
            </h1>
            <p className="text-lg text-[#f5f3ee]/80 max-w-xl mx-auto mb-12 font-light leading-relaxed">
              Curated padel experiences in the stunning Algarve. 
              World-class courts, refined coaching, unforgettable moments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/experiences"
                className="h-14 px-10 bg-[#f5f3ee] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#8ca87c] hover:text-[#f5f3ee]"
                data-testid="hero-explore-btn"
              >
                Explore Experiences
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="h-14 px-10 border border-[#f5f3ee]/50 text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:bg-[#f5f3ee]/10"
                data-testid="hero-book-btn"
              >
                Book Your Trip
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-[#f5f3ee]/60 to-transparent" />
        </motion.div>
      </section>

      {/* Packages Section */}
      <section className="py-28 md:py-36 bg-[#f5f3ee]" data-testid="packages-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Most Popular
            </span>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#414b3b] mb-6">
              Our Favourite Experiences
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light leading-relaxed">
              Discover our most loved padel holiday packages, chosen by our guests.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {packages.slice(0, 2).map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white overflow-hidden group"
                data-testid={`package-card-${index}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={pkg.image_url}
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-2 bg-white text-[#414b3b] text-sm font-light tracking-wider">
                      From €{pkg.price_from}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#f5f3ee] text-[#414b3b]/70 text-xs tracking-wider uppercase flex items-center gap-1">
                      <Clock size={12} />
                      {pkg.duration}
                    </span>
                    <span className="px-3 py-1 bg-[#f5f3ee] text-[#414b3b]/70 text-xs tracking-wider uppercase flex items-center gap-1">
                      <TrendingUp size={12} />
                      {pkg.level}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl text-[#414b3b] mb-3">
                    {pkg.name}
                  </h3>
                  <p className="text-[#414b3b]/60 mb-6 font-light leading-relaxed line-clamp-2">
                    {pkg.description}
                  </p>
                  <Link
                    to={`/experiences/${pkg.id}`}
                    className="w-full h-12 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#8ca87c]"
                    data-testid={`view-package-${index}`}
                  >
                    View Package
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link
              to="/experiences"
              className="inline-flex items-center gap-3 text-[#414b3b] font-light tracking-[0.15em] uppercase text-sm hover:text-[#8ca87c] transition-colors"
              data-testid="view-all-packages"
            >
              View All Packages
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Team / Partners Section */}
      <section className="py-28 md:py-36 bg-white" data-testid="team-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              The Team
            </span>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#414b3b] mb-6">
              Who's Behind It
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light leading-relaxed">
              A partnership between passionate padel players and one of the Algarve's 
              premier padel academies.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Academy Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center justify-center text-center"
            >
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden mb-6 bg-black shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/p61glcjj_Screenshot%202026-02-07%20125309.png"
                  alt="GlobalX Padel Academy"
                  className="w-full h-full object-contain p-4"
                />
              </div>
              <h3 className="font-heading text-2xl text-[#414b3b] mb-3">
                Official Partner Academy
              </h3>
              <p className="text-[#414b3b]/60 font-light leading-relaxed max-w-xs">
                World-class facilities and professional coaching in the heart of the Algarve.
              </p>
            </motion.div>

            {/* Founder Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex flex-col items-center justify-center text-center"
            >
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden mb-6 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/320i44l3_WhatsApp%20Image%202026-02-07%20at%2012.21.36.jpeg"
                  alt="Founder playing padel"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading text-2xl text-[#414b3b] mb-4">
                Your Host
              </h3>
              <div className="space-y-2 mb-4">
                <p className="text-[#8ca87c] font-medium text-sm tracking-wide">Top 20 Best Players of Portugal</p>
                <p className="text-[#414b3b]/70 font-light text-sm">FIP Player</p>
                <p className="text-[#414b3b]/70 font-light text-sm">+8 Years Coaching Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Destination - Algarve */}
      <section className="py-28 md:py-36 bg-[#414b3b]" data-testid="featured-destination-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Grid */}
            <div className="relative grid grid-cols-12 grid-rows-6 gap-3 h-[600px]">
              <div className="col-span-7 row-span-4 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/9vo17oa0_WhatsApp%20Image%202026-02-07%20at%2012.21.37.jpeg"
                  alt="Padel courts at GlobalX Academy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="col-span-5 row-span-3 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/kis88z2s_alvor-baia-resort-hotel-0.jpg"
                  alt="Alvor Baia Resort"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="col-span-5 row-span-3 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/p91krkg7_tivoli_alvor_algarve_resort_facade_2.jpg"
                  alt="Tivoli Alvor Resort"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="col-span-7 row-span-2 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/pox9gsl3_WhatsApp%20Image%202026-02-07%20at%2012.21.45%20%281%29.jpeg"
                  alt="Group of padel players"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
                Featured Destination
              </span>
              <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#f5f3ee] mb-8 leading-tight">
                Discover the Magic of<br />
                <span className="italic">Algarve, Portugal</span>
              </h2>
              <p className="text-lg text-[#f5f3ee]/70 mb-10 font-light leading-relaxed">
                Golden cliffs, pristine beaches, and world-class padel facilities make the 
                Algarve the ultimate destination. Experience the perfect blend of sport, 
                culture, and refined relaxation.
              </p>
              
              <ul className="space-y-4 mb-12">
                {[
                  'Premium padel academies with ocean views',
                  'Gourmet Portuguese cuisine & wine',
                  'Year-round Mediterranean climate',
                  'Stunning coastal experiences',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <span className="text-[#8ca87c] text-lg">✦</span>
                    <span className="text-[#f5f3ee]/80 font-light">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/destinations"
                className="inline-flex items-center gap-3 h-14 px-8 border border-[#8ca87c] text-[#f5f3ee] font-light tracking-[0.15em] uppercase text-sm hover:bg-[#8ca87c] hover:text-[#414b3b] transition-all duration-300"
                data-testid="explore-algarve-btn"
              >
                Explore Algarve
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 md:py-36 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Testimonials
            </span>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#414b3b] mb-6">
              What Our Guests Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#f5f3ee] p-10"
                data-testid={`testimonial-${index}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#8ca87c] text-[#8ca87c]" />
                  ))}
                </div>
                
                <p className="text-[#414b3b]/70 text-lg leading-relaxed mb-8 font-light">
                  "{testimonial.text}"
                </p>
                
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.avatar_url}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-heading text-[#414b3b]">
                      {testimonial.name}
                    </p>
                    <p className="text-[#414b3b]/40 text-sm font-light">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Experience */}
      <section className="py-28 md:py-36 bg-[#f5f3ee]" data-testid="experience-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              The Experience
            </span>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#414b3b] mb-6">
              More Than Just Padel
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light leading-relaxed">
              We curate exceptional experiences that blend world-class sport 
              with refined travel, creating memories that endure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              {
                title: 'Premium Destinations',
                description: 'Handpicked locations with stunning scenery and exceptional facilities.',
              },
              {
                title: 'Level Matching',
                description: 'Play with others at your skill level for the perfect balance.',
              },
              {
                title: 'All-Inclusive',
                description: 'Accommodation, dining, coaching — all thoughtfully arranged.',
              },
              {
                title: 'Expert Coaching',
                description: 'Learn from professionals and elevate your game.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
                data-testid={`value-card-${index}`}
              >
                <div className="text-5xl font-heading font-light text-[#8ca87c]/30 mb-4 group-hover:text-[#8ca87c]/60 transition-colors duration-500">
                  0{index + 1}
                </div>
                <h3 className="font-heading text-xl text-[#414b3b] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#414b3b]/50 font-light text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 bg-[#414b3b]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#f5f3ee] mb-6">
              Ready to Play?
            </h2>
            <p className="text-lg text-[#f5f3ee]/60 mb-12 max-w-xl mx-auto font-light">
              Join discerning players who have discovered their perfect 
              padel retreat in the Algarve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-14 px-10 bg-[#f5f3ee] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#8ca87c]"
                data-testid="cta-book-btn"
              >
                Book Your Trip
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/experiences"
                className="h-14 px-10 border border-[#f5f3ee]/30 text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:border-[#f5f3ee] hover:bg-[#f5f3ee]/10"
                data-testid="cta-explore-btn"
              >
                View Packages
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
